import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Redirect } from "react-router-dom";
import { useSelector, shallowEqual } from "react-redux";
import { Grid } from "@material-ui/core";
import moment from "moment-timezone";

// Hooks
import { useFeatureFlag } from "../../../hooks/useFeatureFlags";
import useCurrentFacility from "../../../hooks/useCurrentFacility";
import useCurrentFacilityTimezone from "../../../hooks/useCurrentFacilityTimezone";
import useCancellationToken from "../../../hooks/useCancellationToken";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import useLPRSearch from "../../../hooks/useLPRSearch";
import useContextDevices from "../../../hooks/useContextDevices";

// Services
import { lprSearchServiceInstance as lprSearchService } from "../../../services/LPRSearchService";

// Components
import LPRSearchFilters from "../../../components/LPRSearch/LPRSearchFilters";
import LPRSearchResults from "../../../components/LPRSearch/LPRSearchResults";
import LPRImageDetailView from "../../../components/LPRSearch/LPRImageDetailView";

const PAGE_SIZE = 10;

/**
 * Pure function to determine if the search button should be enabled.
 * Validates plate text length, illegal patterns, device selection, and date validity.
 */
export const isSearchValid = (filters, facilityTimeZone) => {
  const { plateText, selectedDevices, startDateTime, endDateTime } = filters;

  const trimmedPlateText = plateText.trim();
  const hasValidPlateText =
    trimmedPlateText.length >= 3 && trimmedPlateText.length <= 20 && !trimmedPlateText.includes("%%%");
  const hasDeviceSelected = selectedDevices.length > 0;
  const endOfToday = moment.tz(facilityTimeZone || moment.tz.guess()).endOf("day");
  const hasValidDates =
    startDateTime &&
    endDateTime &&
    moment.isMoment(startDateTime) &&
    moment.isMoment(endDateTime) &&
    startDateTime.isValid() &&
    endDateTime.isValid() &&
    startDateTime.isBefore(endDateTime) &&
    !startDateTime.isAfter(endOfToday) &&
    !endDateTime.isAfter(endOfToday);

  return hasValidPlateText && hasDeviceSelected && hasValidDates;
};

const LPRSearchPage = () => {
  const lprFeatureFlag = useFeatureFlag("LPR");
  const { facilityID } = useCurrentFacility();
  const { timeZone } = useCurrentFacilityTimezone();
  const enqueueSnackbar = useEnqueueSnackbar();

  // Facility scope state from Redux
  const scopeAwareFacilityID = useSelector(
    (state) => state.entityScope?.facilityGroupId || facilityID,
    shallowEqual
  );
  const selectedFacilities = useSelector(
    (state) => state.entityScope?.selected ?? [],
    shallowEqual
  );
  const facilityGroupId = useSelector(
    (state) => state.entityScope?.facilityGroupId ?? null,
    shallowEqual
  );

  // Derive available LPR devices using the existing useContextDevices hook.
  // Passing true for selectedOnly so it respects the facility scope selection.
  // We filter to Entry/Exit (gate) devices locally because the hook's deviceType
  // parameter does not filter correctly for facility groups.
  const contextDevices = useContextDevices(true);
  const availableDevices = useMemo(() => {
    if (!contextDevices || contextDevices.length === 0) return [];
    const devices = [];
    for (const facility of contextDevices) {
      if (!facility.devices) continue;
      for (const device of facility.devices) {
        // Only include Entry/Exit (gate) devices — exclude Door, Kiosk, etc.
        if (device.deviceMode === "Entry" || device.deviceMode === "Exit") {
          devices.push({
            deviceId: device.entityid,
            deviceName: device.name,
            facilityId: facility.id,
          });
        }
      }
    }
    return devices;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(contextDevices)]);

  // Search hook
  const {
    results,
    totalCount,
    isLoading,
    hasSearched,
    error,
    search,
    clearResults,
  } = useLPRSearch();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Image detail view state
  const [imageDetailOpen, setImageDetailOpen] = useState(false);
  const [selectedRead, setSelectedRead] = useState(null);
  const [plateImageUrl, setPlateImageUrl] = useState(null);
  const [contextImageUrl, setContextImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);

  // Filter state with default dates
  const [filters, setFilters] = useState(() => {
    const tz = timeZone || moment.tz.guess();
    const startOfDay = moment.tz(tz).startOf("day");
    const endOfDay = moment.tz(tz).startOf("day").hours(23).minutes(59);
    return {
      startDateTime: startOfDay,
      endDateTime: endOfDay,
      selectedDevices: [],
      plateText: "",
    };
  });

  // Update default dates when timezone changes
  useEffect(() => {
    if (timeZone) {
      setFilters((prev) => ({
        ...prev,
        startDateTime: moment.tz(timeZone).startOf("day"),
        endDateTime: moment.tz(timeZone).startOf("day").hours(23).minutes(59),
      }));
    }
  }, [timeZone]);

  // Default device selection to all available LPR devices
  useEffect(() => {
    if (availableDevices.length > 0) {
      setFilters((prev) => ({
        ...prev,
        selectedDevices: availableDevices.map((d) => d.deviceId),
      }));
    }
  }, [availableDevices]);

  // Handle facility scope changes: clear results, reset page
  useEffect(() => {
    clearResults();
    setCurrentPage(1);
  }, [selectedFacilities, scopeAwareFacilityID]);

  // Execute search with cancellation token
  const executeSearch = useCancellationToken({
    func: async ({ cancelToken, page }) => {
      const pageToUse = page || currentPage;
      const offset = (pageToUse - 1) * PAGE_SIZE;

      const entityID = facilityGroupId ?? facilityID;

      const params = {
        entityID,
        startDateTime: filters.startDateTime.clone().utc().format(),
        endDateTime: filters.endDateTime.clone().utc().format(),
        deviceIDs: filters.selectedDevices,
        plateText: filters.plateText.trim(),
        limit: PAGE_SIZE,
        offset,
      };

      await search(params, cancelToken);
    },
    errHandleFunc: () => {
      enqueueSnackbar("Failed to search LPR reads", {
        variant: "error",
        tag: "LPRSearchFailed",
      });
    },
  }).execute;

  // Re-execute search when page changes (following ValetInventory pattern).
  // hasSearched is intentionally read via ref to avoid re-triggering this effect
  // when it changes — we only want to fire on page changes after an initial search.
  const hasSearchedRef = useRef(hasSearched);
  hasSearchedRef.current = hasSearched;
  useEffect(() => {
    if (hasSearchedRef.current) {
      executeSearch({ page: currentPage });
    }
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle search button click
  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    executeSearch({ page: 1 });
  }, [executeSearch]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  // Handle filter changes
  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  // Handle image click - open detail view and fetch images
  const handleImageClick = useCallback(
    async (lprRead) => {
      // Revoke previous URLs to prevent memory leaks
      if (plateImageUrl) URL.revokeObjectURL(plateImageUrl);
      if (contextImageUrl) URL.revokeObjectURL(contextImageUrl);

      setSelectedRead(lprRead);
      setImageDetailOpen(true);
      setImageLoading(true);
      setPlateImageUrl(null);
      setContextImageUrl(null);

      try {
        const [captureResponse, contextResponse] = await Promise.allSettled([
          lprRead.isImageAvailable
            ? lprSearchService.getCaptureImage(lprRead.lprId)
            : Promise.reject("no capture image"),
          lprSearchService.getContextImage(lprRead.lprId),
        ]);

        if (captureResponse.status === "fulfilled" && captureResponse.value?.data) {
          const blob = new Blob([captureResponse.value.data], { type: "image/jpeg" });
          setPlateImageUrl(URL.createObjectURL(blob));
        }

        if (contextResponse.status === "fulfilled" && contextResponse.value?.data) {
          const blob = new Blob([contextResponse.value.data], { type: "image/jpeg" });
          setContextImageUrl(URL.createObjectURL(blob));
        }
      } catch (err) {
        // Images will show placeholder if URLs remain null
      } finally {
        setImageLoading(false);
      }
    },
    [lprSearchService]
  );

  // Handle image detail view close
  const handleImageDetailClose = useCallback(() => {
    if (plateImageUrl) URL.revokeObjectURL(plateImageUrl);
    if (contextImageUrl) URL.revokeObjectURL(contextImageUrl);
    setImageDetailOpen(false);
    setSelectedRead(null);
    setPlateImageUrl(null);
    setContextImageUrl(null);
  }, [plateImageUrl, contextImageUrl]);

  // Revoke previous object URLs in handleImageClick before setting new ones.
  // Also revoke on close. No useEffect needed — cleanup is explicit.

  // Show error snackbar on search error
  useEffect(() => {
    if (error) {
      enqueueSnackbar("Failed to search LPR reads", {
        variant: "error",
        tag: "LPRSearchFailed",
      });
    }
  }, [error]);

  // Feature flag guard - redirect if LPR is disabled
  if (!lprFeatureFlag) {
    return <Redirect to="/home" />;
  }

  // Determine if search is valid (no facilities selected in scope disables search)
  const searchEnabled =
    selectedFacilities.length > 0 || !facilityGroupId
      ? isSearchValid(filters, timeZone)
      : false;

  return (
    <Grid container>
      <Grid item xs={12}>
        <LPRSearchFilters
          filters={filters}
          availableDevices={availableDevices}
          onFiltersChange={handleFiltersChange}
          onSearch={handleSearch}
          isSearchEnabled={searchEnabled}
          isLoading={isLoading}
          timeZone={timeZone}
        />
      </Grid>

      <Grid item xs={12}>
        <LPRSearchResults
          results={results}
          currentPage={currentPage}
          totalCount={totalCount}
          onPageChange={handlePageChange}
          onImageClick={handleImageClick}
          hasSearched={hasSearched}
          isLoading={isLoading}
          timezone={timeZone}
        />
      </Grid>

      <LPRImageDetailView
        open={imageDetailOpen}
        onClose={handleImageDetailClose}
        plateImageUrl={plateImageUrl}
        contextImageUrl={contextImageUrl}
        plateText={selectedRead?.plateText}
        isLoading={imageLoading}
      />
    </Grid>
  );
};

export default LPRSearchPage;
